import React, { useEffect } from 'react';
import _ from 'lodash';
import store, { brokerClientActions } from '@apply/adminStore';
import { Accordion, SortableHeader, InformModal } from '../../common/SharedComponents';
import { BrokerClientApi, FileApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { useSelector } from 'react-redux';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { handleAdminInfo, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faArrowCircleDown, faHourglassHalf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { faEye } from '@fortawesome/free-regular-svg-icons';

const activeColumns = [
    { key: 'Flgd', label: 'Flag', id: 'isFlagged' },
    { key: 'Broker', label: 'Broker' },
    {
        key: 'Point of Contact',
        label: 'Point of Contact',
        id: 'pointOfContact',
    },
    { key: 'B-Client Flgd', label: 'Flag', id: 'clientIsFlagged' },
    { key: 'B-Client (Rep.)', label: 'B-Client (Rep.)', id: 'personName' },
    { key: 'Business', label: 'Business', id: 'businessName' },
    {
        key: 'Broker Activated By',
        label: 'Broker Activated By',
        id: 'brokerActivatedBy',
    },
];

const PendingBrokerClientTable = () => {
    const searchState = useSelector((state) => state.searchState);
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);
    const { dispatch } = store;

    const refreshEvents = new RefreshEvents();

    const [isLoading, set_isLoading] = React.useState(false);
    const [openCreatePdfModal, set_openCreatePdfModal] = React.useState(false);
    const [createPdfModalText, set_createPdfModalText] = React.useState(false);

    const _handleRowClick = (row) => {
        dispatch(brokerClientActions.setBrokerClientId({ brokerClientId: row.id }));
        dispatch(
            brokerClientActions.setBrokerClientOpen({
                openBrokerClientModal: true,
            })
        );
    };

    const _handleReviewButton = (brokerClientId) => {
        dispatch(
            brokerClientActions.setBrokerClientId({
                brokerClientId: brokerClientId,
            })
        );
        dispatch(
            brokerClientActions.setBrokerClientOpen({
                openBrokerClientModal: true,
            })
        );
    };

    const _loadBrokerClients = async () => {
        const response = await brokerClientApi.getPendingBrokerClientGridRows();
        if (response.statusCode !== 200) return;

        const dataRows = response.result;
        dataRows.forEach((row) => {
            if (row.generatingIccaPdfUrl) {
                const ws = new WebSocket(row.generatingIccaPdfUrl);
                ws.onopen = () => {
                    handleAdminInfo('Generating PDF...');
                };

                ws.onmessage = async (event) => {
                    await _loadBrokerClients();
                    handleAdminSuccess('ICCA PDF Ready for download');
                };
            }
        });

        set_rows(dataRows);
    };

    const [rows, set_rows] = React.useState([]);
    const [sort, set_sort] = React.useState({
        column: 'name',
        direction: 'asc',
    });

    const matchName = (s) => s.name && s.name.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const matchPersonName = (s) =>
        s.personName && s.personName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());
    const matchBusinessName = (s) =>
        s.businessName && s.businessName.toLowerCase().includes(searchState.searchText.brokerClient.toLowerCase());

    const sortedRows = _.chain(rows)
        .filter((s) => !searchState.searchText.brokerClient || matchName(s) || matchPersonName(s) || matchBusinessName(s))
        .orderBy(sort.column, sort.direction)
        .value();

    useEffect(() => {
        async function load() {
            refreshEvents.addRefreshListener(_loadBrokerClients);
            await _loadBrokerClients();
        }

        load();
    }, []);

    const styles = {
        nameColumn: {
            width: 300,
        },
    };

    const _downloadIcca = async (iccaDownloadToken) => {
        const response = await fileApi.downloadUrl({
            token: iccaDownloadToken,
        });

        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const _createIcca = async (id) => {
        try {
            set_isLoading(true);
            const response = await brokerClientApi.createBrokerAgreementPdf(id);
            set_isLoading(false);
            if (response?.statusCode === 200) {
                await _loadBrokerClients();
            } else {
                set_createPdfModalText('The system was not able to process this PDF. Please contact support.');
                set_openCreatePdfModal(true);
            }
        } catch {
            set_createPdfModalText('The system was not able to process this PDF. Please contact support.');
            set_openCreatePdfModal(true);
        } finally {
            set_isLoading(false);
        }
    };

    const _handleCreatePdfConfirm = () => {
        set_openCreatePdfModal(false);
    };

    return (
        <Accordion title='Pending ICCA (waiting on BFI)'>
            <div className='card'>
                <div className='card-section'>
                    <table>
                        <thead>
                            <tr>
                                <th className='count' />
                                {activeColumns.map((col) => (
                                    <SortableHeader
                                        key={col.key}
                                        column={col}
                                        sort={sort}
                                        onPress={set_sort}
                                    />
                                ))}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row) => (
                                <tr key={row.id}>
                                    <td
                                        className='count'
                                        style={{ position: 'relative' }}
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        <span
                                            className='reddot'
                                            style={{ left: 12 }}
                                        >
                                            <span className='sr-only'>Needs attention</span>
                                        </span>
                                    </td>
                                    <td
                                        className='isFlagged'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.isFlagged && (
                                            <i
                                                style={{ color: 'darkred' }}
                                                title='Broker is Flagged!'
                                            >
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                        )}
                                    </td>
                                    <td
                                        className='primary'
                                        style={styles.nameColumn}
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.name}
                                    </td>

                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.pointOfContact}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.clientIsFlagged && (
                                            <i
                                                style={{ color: 'darkred' }}
                                                title='B-Client is Flagged!'
                                            >
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                        )}
                                    </td>
                                    <td
                                        className='secondary'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.personName}
                                    </td>
                                    <td
                                        className='secondary time-stamp'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.businessName}
                                    </td>
                                    <td
                                        className='secondary time-stamp'
                                        onClick={() => _handleRowClick(row)}
                                    >
                                        {row.brokerActivatedBy}
                                    </td>
                                    <td className='secondary actions'>
                                        <button
                                            className='gridButton'
                                            onClick={() => _handleReviewButton(row.id)}
                                        >
                                            <FontAwesomeIcon icon={faEye} /> Review
                                        </button>
                                        {row.iccaDownloadToken && !row.generatingIccaPdfUrl && (
                                            <button
                                                className='gridButton'
                                                onClick={() => _downloadIcca(row.iccaDownloadToken)}
                                            >
                                                <FontAwesomeIcon icon={faArrowCircleDown} /> Download
                                            </button>
                                        )}
                                        {row.generatingIccaPdfUrl && (
                                            <span
                                                style={{
                                                    color: 'black',
                                                    width: '90px',
                                                    textAlign: 'center',
                                                    display: 'inline-block',
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    title='ICCA PDF is generating...'
                                                    icon={faHourglassHalf}
                                                />
                                            </span>
                                        )}
                                        {!row.iccaDownloadToken && !row.generatingIccaPdfUrl && (
                                            <button
                                                className='gridButton red-text'
                                                onClick={() => _createIcca(row.id)}
                                            >
                                                <FontAwesomeIcon icon={faFileExcel} /> Create PDF
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <InformModal
                isOpen={openCreatePdfModal}
                confirmCallback={_handleCreatePdfConfirm}
                text={createPdfModalText}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={isLoading}
                message={'Generating PDF...'}
            />
        </Accordion>
    );
};

export default PendingBrokerClientTable;
